import type { FormConfig, FormVariant } from "./types";
import { hashToUnit, randomId, storageGet, storageSet } from "./utils";

const VISITOR_KEY = "hrn_forms_visitor";

function getVisitorId(): string {
  let id = storageGet(localStorage, VISITOR_KEY);
  if (!id) {
    id = randomId();
    storageSet(localStorage, VISITOR_KEY, id);
  }
  return id;
}

/** Stored as "<round>:<variant id>"; values written before rounds existed are a bare id (round 0). */
function parseStored(value: string | null): { round: number; id: string } | null {
  if (!value) return null;
  const match = /^(\d+):(.+)$/.exec(value);
  return match ? { round: Number(match[1]), id: match[2] } : { round: 0, id: value };
}

/**
 * Picks a variant for this visitor, weighted by `variant.weight` (default 1).
 * The pick is deterministic per (visitor, form, round) and cached in localStorage,
 * so a returning visitor keeps seeing the same variant for the length of a round.
 * A new round (config.round changed) re-picks, with the round in the hash so the
 * same half of visitors does not land on the champion every time.
 */
export function pickVariant(config: FormConfig): FormVariant {
  const round = config.round ?? 0;
  const storageKey = `hrn_forms_variant_${config.id}`;
  const stored = parseStored(storageGet(localStorage, storageKey));
  const byId = stored && stored.round === round ? config.variants.find((v) => v.id === stored.id) : undefined;
  if (byId) return byId;

  const weights = config.variants.map((v) => v.weight ?? 1);
  const total = weights.reduce((a, b) => a + b, 0);
  // The round goes first: this hash (FNV-1a) barely moves when only its last
  // characters change, and a new round has to reshuffle who lands where.
  const seed = round ? `${round}:${config.id}:${getVisitorId()}` : `${getVisitorId()}:${config.id}`;
  const point = hashToUnit(seed) * total;

  let cumulative = 0;
  let picked = config.variants[0];
  for (let i = 0; i < config.variants.length; i++) {
    cumulative += weights[i];
    if (point < cumulative) {
      picked = config.variants[i];
      break;
    }
  }

  storageSet(localStorage, storageKey, `${round}:${picked.id}`);
  return picked;
}
